import { useCallback, useEffect, useState } from 'react';
import { IArtist } from '@/data/models/artist';
import { IOptions } from '@/data/models/option';
import { IMixedField } from '@/data/models/trackMixed';
import _, { isEmpty, isNil } from 'lodash';

interface UseArtistsProps {
  initialArtists: IArtist[] | IMixedField[] | null | undefined;
  originalArtist?: IMixedField[] | null | undefined;
  onChange: (artists: IArtist[] | IMixedField[] | null | undefined) => void;
  defaultRole: string;
  multiTrack?: boolean;
}

export const useArtistContainer = ({
  initialArtists,
  originalArtist,
  onChange,
  defaultRole,
  multiTrack,
}: UseArtistsProps) => {
  const [inputs, setInputs] = useState<any>(initialArtists ?? []);
  /**
   * Reindex sequence for all roles.
   * Returns true if any sequence was changed.
   */
  const sortSequenceInPlaceAll = useCallback(
    (artists: any[]) => {
      let changed = false;
      let index = 0;
      artists.forEach((artist) => {
        const target = multiTrack ? artist.value : artist;
        if (target && multiTrack && artist.isMixed) {
          target.sequence = 0;
        } else if (target && target.sequence !== index) {
          target.sequence = index;
          changed = true;
        }
        index++;
      });
      return changed;
    },
    [multiTrack]
  );

  useEffect(() => {
    if (!isNil(initialArtists) && !isEmpty(initialArtists)) {
      // Deep copy to avoid mutating the original props
      const normalizedArtists = initialArtists.map((a: any) => ({ ...a }));
      setInputs(normalizedArtists);
    } else {
      if (multiTrack) {
        setInputs([
          {
            value: { performerRole: defaultRole, name: '', sequence: 0 },
            originalValue: null,
            isMixed: false,
            isReadOnly: false,
          },
        ]);
      } else {
        setInputs([{ performerRole: defaultRole, name: '', sequence: 0 }]);
      }
    }
  }, [initialArtists, defaultRole, multiTrack]);

  const handleAddInput = useCallback(() => {
    let newArtists = [];
    if (multiTrack) {
      newArtists = [
        ...inputs.map((a: any) => ({ ...a })),
        {
          value: {
            performerRole: defaultRole,
            name: '',
            sequence: inputs.length,
          },
          originalValue: null,
          isMixed: false,
          isReadOnly: false,
        },
      ];
    } else {
      newArtists = [
        ...inputs.map((a: any) => ({ ...a })),
        { performerRole: defaultRole, name: '', sequence: inputs.length },
      ];
    }
    sortSequenceInPlaceAll(newArtists);
    setInputs(newArtists);
    onChange(newArtists);
  }, [inputs, defaultRole, sortSequenceInPlaceAll, onChange, multiTrack]);

  const handleRoleAndSequenceChange = useCallback(
    (selectedOption: IOptions, index: number) => {
      const newRole = selectedOption ? selectedOption.label : '';
      const newArtists = _.cloneDeep(inputs);

      if (multiTrack) {
        if (!newArtists[index].value) {
          newArtists[index].value = {
            performerRole: newRole,
            name: null,
            sequence: index,
          };
        } else {
          newArtists[index].value.performerRole = newRole;
        }
      } else {
        newArtists[index].performerRole = newRole;
      }
      sortSequenceInPlaceAll(newArtists);

      setInputs(newArtists);
      onChange(newArtists);
    },
    [inputs, sortSequenceInPlaceAll, onChange, multiTrack]
  );

  const handleChange: any = useCallback(
    (event: any, index: number) => {
      const { value } = event.target;
      const onChangeValue = _.cloneDeep(inputs);

      if (multiTrack) {
        if (!onChangeValue[index].value) {
          onChangeValue[index].value = {
            performerRole: null,
            name: null,
            sequence: index,
          };
        }
        onChangeValue[index].value.name = value;
      } else {
        onChangeValue[index].name = value;
      }
      sortSequenceInPlaceAll(onChangeValue);

      setInputs(onChangeValue);
      onChange(onChangeValue);
    },
    [inputs, sortSequenceInPlaceAll, onChange, multiTrack]
  );

  const handleDeleteInput = useCallback(
    (index: number) => {
      let newArtists = _.cloneDeep(inputs);
      if (newArtists.length === 1) {
        if (multiTrack) {
          if (isEmpty(originalArtist)) {
            return undoChanges();
          } else {
            newArtists[0] = {
              value: { performerRole: defaultRole, name: '', sequence: 0 },
              originalValue: {
                performerRole: defaultRole,
                name: '',
                sequence: 0,
              },
              isMixed: false,
              isReadOnly: false,
            };
          }
        } else {
          newArtists = [];
        }
      } else {
        newArtists.splice(index, 1);
      }
      sortSequenceInPlaceAll(newArtists);
      setInputs(newArtists);
      onChange(newArtists);
    },
    [inputs, defaultRole, sortSequenceInPlaceAll, onChange, multiTrack]
  );

  const undoChanges = useCallback(() => {
    onChange(_.cloneDeep(originalArtist));
  }, [originalArtist]);

  return {
    inputs,
    handleAddInput,
    handleRoleAndSequenceChange,
    handleChange,
    handleDeleteInput,
    undoChanges,
  };
};
